import { type AnyZod, innerOf, inspect, MAX_UNWRAP_DEPTH, shapeEntries, TRANSPARENT_WRAPPERS } from '../schema/introspect'
import { encodeChecks, messageOf } from './checks'
import { type FieldJSON, type FieldType, type MetaJSON, type ParamsSchemaJSON, SCHEMA_JSON_VERSION } from './types'
import { toJsonValue } from './values'

// zod → wire. Reads the same `_zod.def` internals the rest of the framework reads (see ../schema/introspect).

/**
 * The core types with a wire form of their own.
 *
 * Kept in step with `SUPPORTED_TYPES` (../schema/assert-supported-schema.ts) — the set registration holds an
 * author to. `any` and `unknown` are supported declarations that deliberately have no form here: they model
 * "anything", which is what the `unknown` wire type already means, so they encode to it WITHOUT an
 * `unsupported` breadcrumb (see `problemsFor`).
 */
const FIELD_TYPES = new Set<string>([
  'string', 'number', 'bigint', 'boolean', 'date', 'enum', 'literal', 'object', 'array', 'record',
])

// `union` is not in FIELD_TYPES: `inspect()` resolves a union to its first branch, so it never arrives as a
// declared type. It is detected by the branch list instead (see `encodeField`).
const UNION_TYPE = 'union' as FieldType

// Declarations whose whole content is "no constraint". They encode as `unknown` legitimately, not as a gap.
const PASS_THROUGH_TYPES = new Set(['any', 'unknown'])

// Wrappers whose effect the wire form records as a flag, and the one that cannot be recorded at all.
const WRAPPERS = new Set(['optional', 'nullable', 'default'])

interface Unwrapped {
  optional: boolean
  nullable: boolean
  defaultValue: unknown
  hasDefault: boolean
  unsupported: string[]
}

/**
 * Peel the modifier chain, recording WHICH modifiers were present rather than their order.
 *
 * This walk answers only "which absence modifiers were declared". **It does not decide the core type** —
 * `inspect()` does, and `encodeField` reads it from there. Keeping a second opinion on the core here is what
 * let this module fall behind the rest of the framework once already: it kept its own narrower wrapper set,
 * so every declaration the type surface legitimized (`.readonly()`, `.pipe()`, `z.lazy()`, `.nonoptional()`,
 * `z.record()`) still crossed the wire as `unknown` while the published model had it right.
 *
 * Order is safe to lose because the pipeline derives its absence policy by probing the schema for the forms
 * it accepts (see ../schema/field-plan.ts), and the canonical order ./decode.ts rebuilds in preserves that
 * set for every combination — `.optional().nullable()` and `.nullable().optional()` accept the same things.
 */
function unwrap(schema: AnyZod): Unwrapped {
  const result: Unwrapped = {
    optional: false,
    nullable: false,
    defaultValue: undefined,
    hasDefault: false,
    unsupported: [],
  }

  let current: AnyZod | undefined = schema
  // `.nonoptional()` re-asserts a requirement over whatever it wraps — mirrors ../schema/field-plan.ts.
  let sealed = false
  // The same bound `inspect()` puts on its own peel, for the same reason: a `z.lazy()` whose getter returns
  // another wrapper would unwrap forever, and nothing legitimate nests modifiers anywhere near this deep.
  let depth = 0

  while (current?._zod && depth++ < MAX_UNWRAP_DEPTH) {
    const def = current._zod.def ?? {}
    const type = def.type as string

    if (type === 'nonoptional') {
      sealed = true
      current = def.innerType as AnyZod
      continue
    }

    /**
     * Look STRAIGHT THROUGH the modifiers that say nothing about absence — the same set `inspect()` and
     * `declaredAbsence` look through.
     *
     * Without this the walk stopped at the first one and lost every flag beneath it:
     * `z.string().optional().readonly()` encoded as a plain required string, so the console rebuilt a schema
     * that DEMANDED a value the runner accepts as absent — a form that can never be saved, and a violation
     * of the one-directional invariant in ./types.ts. The core type was moved to `inspect()` and this walk
     * was left behind; it is the same drift, one function along.
     */
    if (TRANSPARENT_WRAPPERS.has(type)) {
      current = innerOf(def, type)
      continue
    }

    if (sealed) {
      break
    }

    if (!WRAPPERS.has(type)) {
      // `.prefault()` runs before parsing rather than in place of an absent value — a different contract, so
      // it is named as unsupported instead of being flattened into `default`.
      if (type === 'prefault' || type === 'nullish') {
        result.unsupported.push(`wrapper:${ type }`)
        current = def.innerType as AnyZod
        continue
      }

      break
    }

    if (type === 'optional') {
      result.optional = true
    }

    if (type === 'nullable') {
      result.nullable = true
    }

    if (type === 'default' && !result.hasDefault) {
      result.hasDefault = true
      result.defaultValue = def.defaultValue
    }

    current = def.innerType as AnyZod
  }

  return result
}

// Author metadata, dropping the keys that carry no meaning on the wire. `frLabels` values are declared by the
// author and may be any JSON; `frDictionary` is a plain ref.
function encodeMeta(meta: Record<string, unknown>): MetaJSON | undefined {
  const encoded = toJsonValue(meta) as MetaJSON

  return Object.keys(encoded).length ? encoded : undefined
}

// enum members live in a `{ key: value }` map; a literal keeps its own `values` array.
function encodeValues(core: AnyZod | undefined, type: FieldType): FieldJSON['values'] {
  if (type === 'enum') {
    const entries = (core?._zod?.def?.entries as Record<string, unknown> | undefined) ?? {}

    return Object.values(entries).map(toJsonValue)
  }

  if (type === 'literal') {
    const values = (core?._zod?.def?.values as unknown[] | undefined) ?? []

    return values.map(toJsonValue)
  }

  return undefined
}

/**
 * One declared field → its wire form.
 *
 * `seen` holds the declarations this field is currently INSIDE — the ancestors on the walk, not everything
 * already encoded — so a schema that legitimately appears twice side by side is still encoded twice. It is
 * added before descending and removed after, which makes the check below a cycle test rather than a
 * deduplication.
 */
export function encodeField(name: string, schema: AnyZod, seen: Set<unknown> = new Set()): FieldJSON {
  /**
   * A self-referential declaration contains itself, so the structural walk below has no bottom.
   *
   * `const Tree = z.lazy(() => z.union([z.string(), z.array(Tree)]))` encodes its union, whose array branch
   * has `Tree` as its element, whose union has an array branch… — and since a `z.lazy()` getter REBUILDS its
   * schema on every call, each level allocates a fresh one and the stack is gone in milliseconds. That was
   * FR-3600: a `RangeError` naming no extension, method or parameter, with the extension never registering.
   * The recursion is structural rather than data-driven, so remembering the ancestors is enough to stop it.
   *
   * The wire form has no way to SAY "and here it is again" — ./decode.ts rebuilds a finite schema — so the
   * repeat is encoded as the pass-through it has to become, with a breadcrumb saying why. Everything above
   * the cycle still crosses intact: `Tree` publishes as the string its first branch declares, and the parts
   * of the declaration that are not recursive keep their own types.
   */
  if (seen.has(schema)) {
    return { name, type: 'unknown', unsupported: ['recursion'] }
  }

  seen.add(schema)

  const { optional, nullable, defaultValue, hasDefault, unsupported } = unwrap(schema)
  // The core, and the type of it, from the one place that decides that for the whole framework.
  const ins = inspect(schema)
  const core = ins.core
  const declaredType = ins.branches ? UNION_TYPE : ins.resolvedType ?? 'unknown'
  const type = (ins.branches || FIELD_TYPES.has(declaredType) ? declaredType : 'unknown') as FieldType
  // `core` is a union's FIRST BRANCH, so its format/checks/message describe that branch and not the union.
  // Encoding them onto the union node would hand `decodeField` constraints to apply to a `ZodUnion`: today
  // they no-op because a union has no `.min()`, which is luck rather than design — the first one that did
  // map to a real method would make the rebuild reject what a later branch accepts. The branches carry their
  // own constraints in `options`, which is where they belong.
  const isUnion = !!ins.branches
  const format = isUnion ? undefined : core?._zod?.def?.format as string | undefined
  const { checks, unsupported: checkIssues } = isUnion ? { checks: [], unsupported: [] } : encodeChecks(core)
  const meta = encodeMeta(ins.meta as Record<string, unknown>)
  const message = isUnion ? undefined : messageOf((core?._zod?.def ?? {}) as { error?: unknown })
  const values = encodeValues(core, type)
  const problems = [...unsupported, ...checkIssues]

  // `unknown` reached by a declaration that MEANS "anything" is the wire form working, not failing.
  if (type === 'unknown' && !PASS_THROUGH_TYPES.has(declaredType)) {
    problems.push(`type:${ declaredType }`)
  }

  const encoded: FieldJSON = {
    name,
    type,
    ...(format ? { format } : {}),
    ...(optional ? { optional: true as const } : {}),
    ...(nullable ? { nullable: true as const } : {}),
    ...(hasDefault ? { default: toJsonValue(defaultValue) } : {}),
    ...(checks.length ? { checks } : {}),
    ...(values?.length ? { values } : {}),
    ...(meta ? { meta } : {}),
    ...(message ? { message } : {}),
    ...(type === 'object' ? { properties: shapeEntries(core).map(([key, inner]) => encodeField(key, inner, seen)) } : {}),
    ...(type === 'array' ? { element: encodeField('', core?._zod?.def?.element as AnyZod, seen) } : {}),
    ...(type === 'record' ? { value: encodeField('', core?._zod?.def?.valueType as AnyZod, seen) } : {}),
    ...(ins.branches ? { options: ins.branches.map(branch => encodeField('', branch, seen)) } : {}),
    ...(problems.length ? { unsupported: problems } : {}),
  }

  // Off the walk again: the field is fully encoded, so this declaration is no longer an ancestor of anything.
  seen.delete(schema)

  return encoded
}

/**
 * A whole declared slot → its wire form. `null` for an undeclared slot, which is legal everywhere: a method
 * with no params publishes no schema rather than an empty one.
 *
 * Takes `unknown` because this is a boundary: callers hold a schema through whichever view they happen to
 * have (`Params.scheme`, an author's `z.object`, a decoded rebuild), and none of those structural views are
 * assignable to each other. Narrowing once here beats a cast at every call site.
 */
export function encodeParams(scheme: unknown): ParamsSchemaJSON | null {
  const schema = scheme as AnyZod | undefined

  if (!schema?._zod) {
    return null
  }

  return {
    v: SCHEMA_JSON_VERSION,
    fields: shapeEntries(schema).map(([name, field]) => encodeField(name, field)),
  }
}
